import { isBackofficePlayground } from '../../config/deployment';
import { logger } from '../core/logger';

type UrlChangeCallback = (url: string) => void;

const listeners = new Set<UrlChangeCallback>();
let isPatched = false;
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;
let popstateHandler: (() => void) | null = null;
let hashchangeHandler: (() => void) | null = null;

function notifyListeners(): void {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    for (const callback of listeners) {
        try {
            callback(url);
        } catch (error) {
            logger.warn('urlChange: listener threw an error', error);
        }
    }
}

function patchHistory(): void {
    if (isPatched || typeof window === 'undefined') return;

    const savedPushState = history.pushState;
    const savedReplaceState = history.replaceState;
    originalPushState = savedPushState;
    originalReplaceState = savedReplaceState;

    history.pushState = (...args: Parameters<History['pushState']>) => {
        const result = savedPushState.apply(history, args);
        notifyListeners();
        return result;
    };

    history.replaceState = (...args: Parameters<History['replaceState']>) => {
        const result = savedReplaceState.apply(history, args);
        notifyListeners();
        return result;
    };

    popstateHandler = () => notifyListeners();
    hashchangeHandler = () => notifyListeners();

    window.addEventListener('popstate', popstateHandler);
    window.addEventListener('hashchange', hashchangeHandler);

    isPatched = true;
}

function unpatchHistory(): void {
    if (!isPatched || typeof window === 'undefined') return;

    if (originalPushState) {
        history.pushState = originalPushState;
    }
    if (originalReplaceState) {
        history.replaceState = originalReplaceState;
    }

    if (popstateHandler) {
        window.removeEventListener('popstate', popstateHandler);
        popstateHandler = null;
    }
    if (hashchangeHandler) {
        window.removeEventListener('hashchange', hashchangeHandler);
        hashchangeHandler = null;
    }

    originalPushState = null;
    originalReplaceState = null;
    isPatched = false;
}

export function subscribeToUrlChanges(callback: UrlChangeCallback): () => void {
    if (typeof window === 'undefined') {
        return () => {};
    }

    // The backoffice playground embeds the widget inside its own SPA, so `window.history`
    // here is the backoffice's react-router history. Patching it corrupts host navigation:
    // the patch leaks across widget mounts and accumulates into a no-op, freezing sidebar
    // nav until a full reload. The simulated preview has no real URL to observe, so skip.
    if (isBackofficePlayground()) {
        return () => {};
    }

    listeners.add(callback);

    if (!isPatched) {
        patchHistory();
    }

    return () => {
        listeners.delete(callback);
        if (listeners.size === 0) {
            unpatchHistory();
        }
    };
}
